import { memo } from 'react'
import {
  IconBrandTelegram,
  IconFileText,
  IconGift,
  IconHeadphones,
  IconHelpCircle,
  IconInfoCircle,
  IconReceiptRefund,
  IconShieldLock,
} from '@tabler/icons-react'
import ScreenShell from './ScreenShell'
import {
  ABOUT_DELIVERY_ITEMS,
  ABOUT_DELIVERY_NOTE,
  ABOUT_DELIVERY_WARNING,
  ABOUT_FAQ_FOOTER,
  ABOUT_FAQ_ITEMS,
  ABOUT_SERVICE_PARAGRAPHS,
  REFUND_POLICY_URL,
} from '../lib/aboutContent'
import { safeOpenLink, safeSheetOpenHaptic } from '../lib/safeTelegram'

function openTelegramLink(url) {
  const link = String(url ?? '').trim()
  if (!link) return

  safeSheetOpenHaptic()

  const tg = window.Telegram?.WebApp
  try {
    if (typeof tg?.openTelegramLink === 'function') {
      tg.openTelegramLink(link)
      return
    }
  } catch (error) {
    console.error('[AboutScreen] openTelegramLink failed', error)
  }

  safeOpenLink(link)
}

function openExternalLink(url) {
  const link = String(url ?? '').trim()
  if (!link) return

  safeSheetOpenHaptic()
  safeOpenLink(link)
}

function AboutSection({ title, icon: Icon, children }) {
  const renderIcon = () => {
    if (Icon && typeof Icon === 'object' && Icon.$$typeof) {
      const typeofSymbol = Icon.$$typeof.toString()
      if (typeofSymbol === 'Symbol(react.element)') {
        return Icon
      }
    }
    if (typeof Icon === 'function' || (Icon && typeof Icon === 'object' && Icon.$$typeof)) {
      const IconComponent = Icon
      return <IconComponent size={18} stroke={2} className="shrink-0 text-accent" />
    }
    return Icon
  }

  return (
    <section className="tma-card premium-card rounded-2xl px-4 py-4">
      <h3 className="flex items-center gap-2 text-[15px] font-semibold text-[var(--color-text)]">
        {renderIcon()}
        {title}
      </h3>
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  )
}

function SupportHero({ href }) {
  if (!href) return null

  return (
    <button
      type="button"
      onClick={() => openTelegramLink(href)}
      className="tma-card flex w-full items-center gap-3 rounded-2xl border-accent/20 bg-accent/10 px-4 py-4 text-left transition-transform active:scale-[0.99]"
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent/15">
        <IconHeadphones size={22} stroke={2} className="text-accent" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-semibold text-accent">Служба поддержки</p>
        <p className="mt-0.5 text-[13px] text-accent/80">Ответим на любые вопросы</p>
      </div>
      <img loading="eager" src="/images/icons/figma/direction-right.svg" alt="" aria-hidden="true" width="24" height="24" className="h-6 w-6 shrink-0" />
    </button>
  )
}

function FooterLinkRow({ label, href, icon: Icon, onClick }) {
  if (!href) return null

  const renderIcon = () => {
    if (Icon && typeof Icon === 'object' && Icon.$$typeof) {
      const typeofSymbol = Icon.$$typeof.toString()
      if (typeofSymbol === 'Symbol(react.element)') {
        return Icon
      }
    }
    if (typeof Icon === 'function' || (Icon && typeof Icon === 'object' && Icon.$$typeof)) {
      const IconComponent = Icon
      return <IconComponent size={18} stroke={2} className="shrink-0 text-[var(--tma-text-secondary)]" />
    }
    return Icon
  }

  return (
    <button
      type="button"
      onClick={() => onClick(href)}
      className="flex w-full items-center gap-3 rounded-xl px-1 py-3 text-left transition-transform active:scale-[0.99]"
    >
      {renderIcon()}
      <span className="min-w-0 flex-1 text-[14px] text-[var(--color-text)]">{label}</span>
      <img loading="eager" src="/images/icons/figma/direction-right.svg" alt="" aria-hidden="true" width="24" height="24" className="h-6 w-6 shrink-0 opacity-50" />
    </button>
  )
}

function AboutScreen({ links = {} }) {
  const supportLink = links.support_link ?? ''
  const channelLink = links.channel_link ?? ''
  const policyUrl = links.policy_url ?? ''
  const agreementUrl = links.agreement_url ?? ''

  const hasFooterLinks = channelLink || policyUrl || agreementUrl || REFUND_POLICY_URL

  return (
    <ScreenShell title="Информация">
      <div className="flow-screen-scroll relative min-h-0 w-full flex-1 space-y-3 overflow-y-auto">
        <SupportHero href={supportLink} />

        <AboutSection title="О сервисе" icon={IconInfoCircle}>
          {ABOUT_SERVICE_PARAGRAPHS.map((paragraph) => (
            <p key={paragraph} className="text-[14px] leading-relaxed text-[var(--color-text-secondary)]">
              {paragraph}
            </p>
          ))}
        </AboutSection>

        <AboutSection title="Как происходит выдача" icon={IconGift}>
          <ul className="space-y-3">
            {ABOUT_DELIVERY_ITEMS.map(({ title, text }) => (
              <li key={title} className="text-[14px] leading-relaxed text-[var(--color-text-secondary)]">
                <span className="font-semibold text-[var(--color-text)]">{title}:</span> {text}
              </li>
            ))}
          </ul>
          <p className="text-[14px] italic leading-relaxed text-[var(--color-text-secondary)]">
            {ABOUT_DELIVERY_NOTE}
          </p>
          <p className="text-[14px] leading-relaxed text-[var(--color-text-secondary)]">
            ⚠️ {ABOUT_DELIVERY_WARNING}
          </p>
        </AboutSection>

        <AboutSection title="Частые вопросы" icon={IconHelpCircle}>
          <div className="space-y-4">
            {ABOUT_FAQ_ITEMS.map(({ question, answer }) => (
              <div key={question}>
                <p className="text-[14px] font-semibold text-[var(--color-text)]">{question}</p>
                <p className="mt-1 text-[14px] leading-relaxed text-[var(--color-text-secondary)]">{answer}</p>
              </div>
            ))}
          </div>
          <p className="text-[14px] leading-relaxed text-[var(--color-text-secondary)]">{ABOUT_FAQ_FOOTER}</p>
        </AboutSection>

        {hasFooterLinks ? (
      <section className="tma-card premium-card rounded-2xl px-3 py-2">
            <FooterLinkRow
              label="Наш канал"
              href={channelLink}
              icon={IconBrandTelegram}
              onClick={openTelegramLink}
            />
            <FooterLinkRow
              label="Политика конфиденциальности"
              href={policyUrl}
              icon={IconShieldLock}
              onClick={openExternalLink}
            />
            <FooterLinkRow
              label="Политика возврата средств"
              href={REFUND_POLICY_URL}
              icon={IconReceiptRefund}
              onClick={openExternalLink}
            />
            <FooterLinkRow
              label="Пользовательское соглашение"
              href={agreementUrl}
              icon={IconFileText}
              onClick={openExternalLink}
            />
          </section>
        ) : null}
      </div>
    </ScreenShell>
  )
}

export default memo(AboutScreen)
